import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import {
  BOLAG,
  KALLOR,
  formatSEK,
  totalKapital,
  offentligaTyper,
  privataTyper,
  type Bolag,
  type ProspectKalla,
  type Bransch,
  type BolagStatus,
} from "@/lib/portfolio-data";
import { Download, Printer, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export const Route = createFileRoute("/strategirapport")({
  head: () => ({
    meta: [
      { title: "Strategirapport — Inkubera" },
      { name: "description", content: "Strategiskt rapportunderlag för styrelse och ägare." },
    ],
  }),
  component: Rapport,
});

const ACTIVE_PHASES = new Set(["Prospect", "Förinkubation", "Inkubation", "Tillväxt"]);

function Rapport() {
  const arNu = new Date().getFullYear();
  const [year, setYear] = useState(arNu);
  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const yearsAvailable = useMemo(() => {
    const set = new Set<number>([arNu]);
    BOLAG.forEach((b) => {
      b.finansiering.forEach((f) => set.add(new Date(f.datum).getFullYear()));
      if (b.inkomDatum) set.add(new Date(b.inkomDatum).getFullYear());
    });
    return [...set].sort((a, b) => b - a);
  }, [arNu]);

  const data = useMemo(() => buildReport(BOLAG, year), [year]);

  const buildPdf = async () => {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"),
      import("html2canvas-pro"),
    ]);
    const node = reportRef.current!;
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;
    let heightLeft = imgH;
    let position = 0;
    pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
    heightLeft -= pageH;
    while (heightLeft > 0) {
      position = heightLeft - imgH;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
      heightLeft -= pageH;
    }
    return pdf;
  };

  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const pdf = await buildPdf();
      pdf.save(`strategirapport-${year}.pdf`);
    } finally {
      setExporting(false);
    }
  };

  const handlePreviewPDF = async () => {
    if (!reportRef.current) return;
    setPreviewing(true);
    try {
      const pdf = await buildPdf();
      const blobUrl = URL.createObjectURL(pdf.output("blob"));
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(blobUrl);
      setPreviewOpen(true);
    } finally {
      setPreviewing(false);
    }
  };

  const downloadFromPreview = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `strategirapport-${year}.pdf`;
    a.click();
  };

  return (
    <AppShell>
      <div ref={reportRef} className="bg-background">
      <section className="px-8 pt-10 pb-6 border-b border-border print:px-0 print:pt-0">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">Strategirapport</div>
            <h1 className="font-display text-4xl">Styrelseunderlag {year}</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Strategiskt rapportunderlag för styrelse- och ägarsammanhang. Visar portföljens fördelning,
              kapitalresning, branschmix, exits och inflöde av prospects under verksamhetsåret.
            </p>
          </div>
          <div className="flex items-center gap-2 print:hidden" data-html2canvas-ignore="true">
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
            >
              {yearsAvailable.map((y) => (
                <option key={y} value={y}>Verksamhetsår {y}</option>
              ))}
            </select>
            <button
              onClick={() => window.print()}
              className="h-10 px-4 rounded-lg bg-secondary text-foreground text-sm font-medium inline-flex items-center gap-2 hover:bg-secondary/80"
            >
              <Printer className="size-4" /> Skriv ut
            </button>
            <button
              onClick={handlePreviewPDF}
              disabled={previewing}
              className="h-10 px-4 rounded-lg bg-secondary text-foreground text-sm font-medium inline-flex items-center gap-2 hover:bg-secondary/80 disabled:opacity-50"
            >
              <Eye className="size-4" /> {previewing ? "Förbereder…" : "Förhandsgranska PDF"}
            </button>
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="h-10 px-4 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
            >
              <Download className="size-4" /> {exporting ? "Genererar PDF…" : "Ladda ner PDF"}
            </button>
          </div>
        </div>
      </section>

      <section className="px-8 py-8 space-y-8 print:px-0">
        {/* Nyckeltal */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Bolag i portfölj" value={data.aktivaBolag.length} sub={`${data.fasCount.Inkubation ?? 0} inkubation · ${data.fasCount.Förinkubation ?? 0} förinkubation`} />
          <KPI label={`Kapital rest ${year}`} value={formatSEK(data.totalKapitalAr)} sub={`${data.finansieringAr.length} rundor`} />
          <KPI label="Offentligt stöd" value={formatSEK(data.offentligtAr)} sub={`${pct(data.offentligtAr, data.totalKapitalAr)} av totalt`} />
          <KPI label="Prospects in" value={data.prospectsAr.length} sub={`Mål: kontinuerligt inflöde`} />
        </div>

        {/* Portfölj per fas */}
        <Card title="Portfölj per fas" desc="Antal aktiva bolag per fas i pipelinen.">
          <BarRow rows={data.fasRows} format={(v) => `${v} st`} />
        </Card>

        {/* Kapital över tid */}
        <Card title={`Kapitalresning per månad (${year})`} desc="Privat kapital och offentligt stöd månadsvis.">
          <MonthlyChart months={data.kapitalPerManad} />
          <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
            <Stat label="Privat kapital" value={formatSEK(data.privatAr)} accent="oklch(0.6 0.18 260)" />
            <Stat label="Offentligt stöd & krediter" value={formatSEK(data.offentligtAr)} accent="oklch(0.7 0.16 130)" />
          </div>
        </Card>

        {/* Branscher */}
        <Card title="Branschmix i inkubator & accelerator" desc="Var våra aktiva bolag verkar idag.">
          <BarRow rows={data.branschRows} format={(v) => `${v} bolag`} />
        </Card>

        {/* Exits & avslutade */}
        <Card title="Exits, avslutade och avvecklade" desc="Bolag som lämnat portföljen eller där avtal avslutats i förtid.">
          {data.exits.length === 0 ? (
            <Empty text="Inga avslut registrerade." />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="py-2 font-medium">Bolag</th>
                  <th className="py-2 font-medium">Status</th>
                  <th className="py-2 font-medium">Fas vid avslut</th>
                  <th className="py-2 font-medium">Branscher</th>
                  <th className="py-2 font-medium text-right">Totalt rest kapital</th>
                </tr>
              </thead>
              <tbody>
                {data.exits.map((b) => (
                  <tr key={b.id} className="border-t border-border">
                    <td className="py-2.5 font-medium">{b.namn}</td>
                    <td className="py-2.5"><StatusPill status={b.status ?? "Aktivt"} /></td>
                    <td className="py-2.5 text-muted-foreground">{b.fas}</td>
                    <td className="py-2.5 text-muted-foreground">{b.branscher.join(", ")}</td>
                    <td className="py-2.5 text-right font-medium">{formatSEK(totalKapital(b))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Prospects-inflöde */}
        <Card title={`Inflöde av prospects (${year})`} desc="Antal nya prospects per månad och fördelning per källa.">
          <MonthlyBars months={data.prospectsPerManad} color="oklch(0.65 0.16 50)" />
          <div className="mt-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Källor</div>
            <BarRow rows={data.kallaRows} format={(v) => `${v} st`} />
          </div>
        </Card>
      </section>
      </div>

      <Dialog open={previewOpen} onOpenChange={(o) => { setPreviewOpen(o); if (!o && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
            <DialogTitle>Förhandsgranskning — Strategirapport {year}</DialogTitle>
            <DialogDescription>Granska PDF:en innan du laddar ner den.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 bg-muted overflow-hidden">
            {previewUrl && (
              <iframe src={previewUrl} title="PDF-förhandsgranskning" className="w-full h-full" />
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-border">
            <button
              onClick={() => setPreviewOpen(false)}
              className="h-10 px-4 rounded-lg bg-secondary text-foreground text-sm font-medium hover:bg-secondary/80"
            >
              Stäng
            </button>
            <button
              onClick={downloadFromPreview}
              className="h-10 px-4 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 hover:opacity-90"
            >
              <Download className="size-4" /> Ladda ner PDF
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

// ---------- helpers ----------

function buildReport(bolag: Bolag[], year: number) {
  const aktivaBolag = bolag.filter((b) => ACTIVE_PHASES.has(b.fas));
  const fasCount: Record<string, number> = {};
  aktivaBolag.forEach((b) => { fasCount[b.fas] = (fasCount[b.fas] ?? 0) + 1; });

  const fasRows = ["Prospect", "Förinkubation", "Inkubation", "Tillväxt"].map((f) => ({
    label: f, value: fasCount[f] ?? 0,
  }));

  // Finansiering under året
  const finansieringAr = bolag.flatMap((b) =>
    b.finansiering.filter((f) => new Date(f.datum).getFullYear() === year).map((f) => ({ ...f, bolag: b.namn })),
  );
  const totalKapitalAr = finansieringAr.reduce((s, f) => s + f.belopp, 0);
  const offentligtAr = finansieringAr.filter((f) => offentligaTyper.includes(f.typ)).reduce((s, f) => s + f.belopp, 0);
  const privatAr = finansieringAr.filter((f) => privataTyper.includes(f.typ)).reduce((s, f) => s + f.belopp, 0);

  // Per månad
  const kapitalPerManad = Array.from({ length: 12 }, (_, i) => {
    const inMonth = finansieringAr.filter((f) => new Date(f.datum).getMonth() === i);
    return {
      label: monthLabel(i),
      privat: inMonth.filter((f) => privataTyper.includes(f.typ)).reduce((s, f) => s + f.belopp, 0),
      offentligt: inMonth.filter((f) => offentligaTyper.includes(f.typ)).reduce((s, f) => s + f.belopp, 0),
    };
  });

  // Branscher
  const branschCount: Record<string, number> = {};
  aktivaBolag.forEach((b) => b.branscher.forEach((br) => { branschCount[br] = (branschCount[br] ?? 0) + 1; }));
  const branschRows = (Object.entries(branschCount) as [Bransch, number][])
    .sort((a, b) => b[1] - a[1])
    .map(([label, value]) => ({ label, value }));

  // Exits & avslutade
  const exits = bolag.filter((b) => b.status && b.status !== "Aktivt" && b.status !== "Inaktivt");

  // Prospects in under året
  const prospectsAr = bolag.filter(
    (b) => b.fas === "Prospect" && b.inkomDatum && new Date(b.inkomDatum).getFullYear() === year,
  );
  const prospectsPerManad = Array.from({ length: 12 }, (_, i) => ({
    label: monthLabel(i),
    value: prospectsAr.filter((p) => new Date(p.inkomDatum!).getMonth() === i).length,
  }));

  // Källor
  const kallaCount: Record<string, number> = {};
  prospectsAr.forEach((p) => {
    const k = p.källa ?? "Annat";
    kallaCount[k] = (kallaCount[k] ?? 0) + 1;
  });
  const kallaRows = KALLOR
    .map((k: ProspectKalla) => ({ label: k, value: kallaCount[k] ?? 0 }))
    .filter((r) => r.value > 0)
    .sort((a, b) => b.value - a.value);

  return {
    aktivaBolag, fasCount, fasRows,
    finansieringAr, totalKapitalAr, offentligtAr, privatAr,
    kapitalPerManad, branschRows, exits, prospectsAr, prospectsPerManad, kallaRows,
  };
}

const monthLabel = (i: number) =>
  ["Jan", "Feb", "Mar", "Apr", "Maj", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"][i];

const pct = (a: number, b: number) => (b === 0 ? "0%" : `${Math.round((a / b) * 100)}%`);

// ---------- UI primitives ----------

function KPI({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function Card({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 print:break-inside-avoid">
      <div className="mb-4">
        <div className="font-display text-lg">{title}</div>
        {desc && <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-background/60 p-3">
      <span className="size-2.5 rounded-full" style={{ background: accent }} />
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-medium">{value}</div>
      </div>
    </div>
  );
}

function BarRow({ rows, format }: { rows: { label: string; value: number }[]; format: (v: number) => string }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-3 text-sm">
          <div className="w-32 shrink-0 text-muted-foreground">{r.label}</div>
          <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full rounded-full bg-foreground" style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
          <div className="w-24 text-right font-medium tabular-nums">{format(r.value)}</div>
        </div>
      ))}
    </div>
  );
}

function MonthlyChart({ months }: { months: { label: string; privat: number; offentligt: number }[] }) {
  const max = Math.max(1, ...months.map((m) => m.privat + m.offentligt));
  return (
    <div className="flex items-end gap-1.5 h-40">
      {months.map((m) => {
        const total = m.privat + m.offentligt;
        const h = (total / max) * 100;
        const privatH = total === 0 ? 0 : (m.privat / total) * 100;
        return (
          <div key={m.label} className="flex-1 flex flex-col items-center gap-1.5">
            <div className="w-full flex-1 flex items-end">
              <div className="w-full rounded-md overflow-hidden flex flex-col justify-end" style={{ height: `${h}%`, minHeight: total > 0 ? 4 : 0 }}>
                <div style={{ background: "oklch(0.7 0.16 130)", height: `${100 - privatH}%` }} />
                <div style={{ background: "oklch(0.6 0.18 260)", height: `${privatH}%` }} />
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground">{m.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function MonthlyBars({ months, color }: { months: { label: string; value: number }[]; color: string }) {
  const max = Math.max(1, ...months.map((m) => m.value));
  return (
    <div className="flex items-end gap-1.5 h-32">
      {months.map((m) => (
        <div key={m.label} className="flex-1 flex flex-col items-center gap-1.5">
          <div className="w-full flex-1 flex items-end">
            <div className="w-full rounded-md" style={{ background: color, height: `${(m.value / max) * 100}%`, minHeight: m.value > 0 ? 4 : 0 }} />
          </div>
          <div className="text-[10px] text-muted-foreground">{m.label}</div>
          <div className="text-[10px] font-medium tabular-nums">{m.value || ""}</div>
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }: { status: BolagStatus }) {
  const styles: Record<BolagStatus, string> = {
    Aktivt: "bg-secondary text-foreground",
    Inaktivt: "bg-secondary text-muted-foreground",
    Avvecklat: "bg-[oklch(0.7_0.04_30)/0.2] text-[oklch(0.5_0.06_30)]",
    Konkurs: "bg-[oklch(0.7_0.2_25)/0.2] text-[oklch(0.55_0.2_25)]",
    Uppköpt: "bg-[oklch(0.7_0.18_280)/0.2] text-[oklch(0.55_0.18_280)]",
  };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full ${styles[status]}`}>{status}</span>;
}

function Empty({ text }: { text: string }) {
  return <div className="text-sm text-muted-foreground italic py-4">{text}</div>;
}
